import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import ListingCard from '../components/ListingCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import LogoDisplay from '../components/LogoDisplay';
import AnnouncementModal from '../components/AnnouncementModal';

const PublicPortal = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ type: 'Todos', operation: 'Todos' });

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: supabaseError } = await supabase
        .from('listings_final_v1')
        .select('*')
        .order('created_at', { ascending: false });

      if (supabaseError) throw supabaseError;

      const now = new Date();
      // Constante de 30 días en milisegundos
      const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

      const activeListings = (data || []).filter(item => {
        // 1. Mostrar siempre si está disponible
        if (item.status === 'Disponible' || !item.status) return true;

        // 2. Si es Vendido/Rentado, verificar antigüedad del cambio
        if (item.status_updated_at) {
          const updatedAt = new Date(item.status_updated_at);
          const diff = now - updatedAt;
          return diff < THIRTY_DAYS_MS;
        }
        
        // 3. Si no tiene fecha de estado pero tiene estado especial, mostrarlo (fallback)
        return true;
      });

      setListings(activeListings);
    } catch (err) {
      console.error("Error al cargar propiedades:", err);
      setError("No se pudieron cargar las propiedades. Asegúrate de ejecutar la migración SQL.");
    } finally {
      setLoading(false);
    }
  };

  const filteredListings = listings.filter(item => {
    const typeMatch = filter.type === 'Todos' || item.property_type === filter.type;
    const opMatch = filter.operation === 'Todos' || item.operation === filter.operation;
    return typeMatch && opMatch;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative">
      {/* Ventana Emergente de Afiliación */}
      <AnnouncementModal />

      <div className="text-center mb-12">
        <div className="flex justify-center mb-6">
          <div className="w-40 h-40 p-4 bg-white rounded-[2.5rem] shadow-xl border-2 border-emerald-50 flex items-center justify-center overflow-hidden">
            <LogoDisplay className="w-full h-full object-contain" />
          </div>
        </div>
        <h1 className="text-4xl md:text-5xl font-black text-emerald-900 mb-4 tracking-tight">
          Propiedades Destacadas en Honduras
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto font-medium">
          Catálogo oficial de Arboleda Bienes Raíces.
        </p>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-10 flex flex-wrap gap-4 items-center justify-center sticky top-20 z-40">
        <select 
          value={filter.type} 
          onChange={(e) => setFilter(p => ({ ...p, type: e.target.value }))}
          className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm outline-none font-bold text-gray-700"
        >
          <option>Todos</option>
          <option>Casa</option>
          <option>Departamento</option>
          <option>Terreno</option>
          <option>Oficina</option>
          <option>Local</option>
        </select>

        <select 
          value={filter.operation} 
          onChange={(e) => setFilter(p => ({ ...p, operation: e.target.value }))}
          className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm outline-none font-bold text-gray-700"
        >
          <option>Todos</option>
          <option>Venta</option>
          <option>Renta</option>
        </select>
        
        <button 
          onClick={fetchListings}
          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          title="Refrescar catálogo"
        >
          <SafeIcon icon={FiIcons.FiRefreshCcw} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error ? (
        <div className="text-center py-20 bg-red-50 rounded-3xl border border-red-100">
          <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-4xl text-red-500 mx-auto mb-4" />
          <p className="text-red-700 font-bold mb-4">{error}</p>
          <button onClick={fetchListings} className="bg-white px-6 py-2 rounded-xl shadow-sm border border-red-200 text-red-700 font-black hover:bg-red-50">
            Reintentar Conexión
          </button>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3].map(i => <div key={i} className="h-96 bg-gray-100 animate-pulse rounded-2xl"></div>)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredListings.map((property) => (
            <ListingCard key={property.id} property={property} />
          ))}
        </div>
      )}

      {!loading && !error && filteredListings.length === 0 && (
        <div className="text-center py-20">
          <SafeIcon icon={FiIcons.FiSearch} className="text-5xl text-gray-200 mx-auto mb-4" />
          <p className="text-gray-400 font-bold uppercase tracking-widest">No hay propiedades disponibles</p>
        </div>
      )}
    </div>
  );
};

export default PublicPortal;